import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldAlert, PackageCheck } from "lucide-react";
import { PageHead, Wrap, H2, P, Prose, Impact, DontList, Callout } from "@/components/Content";
import { ChainOfCustody } from "@/components/ChainOfCustody";

export default function OurProcess() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <PageHead eyebrow="Our Process" title={<>CLEAN. PREP. PRESERVE.</>} tone="cyan"
        sub="Inspection, responsible cleaning where appropriate, careful preparation, preservation and presentation — with documentation at every step." />

      {/* Gentle by design banner */}
      <Wrap className="pt-10">
        <Link to="/trust-center#gentle-by-design" data-testid="op-gentle-banner"
          className="group flex items-center justify-between gap-4 rounded-2xl holo-border holo-glow px-6 py-5">
          <div>
            <p className="eyebrow mb-1">Gentle By Design</p>
            <p className="h-display text-lg sm:text-xl text-chrome">It's not the solution. <span className="text-holo">It's the process.</span></p>
            <p className="text-sm text-[color:var(--chrome-2)] mt-1">Read why our process is careful, restrained and different — in the Trust Center.</p>
          </div>
          <ArrowRight size={22} className="text-[color:var(--cyan)] shrink-0 group-hover:translate-x-1 transition-transform" />
        </Link>
      </Wrap>

      {/* Chain of custody */}
      <Wrap className="py-16">
        <p className="eyebrow mb-5 flex items-center gap-2"><PackageCheck size={13} className="text-[color:var(--cyan)]" /> Chain Of Custody</p>
        <ChainOfCustody />
      </Wrap>

      {/* What we do */}
      <Wrap>
        <H2>WHAT WE DO</H2>
        <Prose className="mt-6">
          <P>CardScience HQ evaluates collectibles individually.</P>
          <P>Our work may include appropriate surface cleaning, careful preparation, preservation-oriented handling, presentation improvement and collectible-specific evaluation.</P>
          <Impact>Our goal is not to manufacture condition that does not exist.</Impact>
          <P>Our goal is to help remove appropriate contaminants or presentation issues when doing so can be performed responsibly.</P>
        </Prose>
        <div className="mt-8">
          <p className="eyebrow mb-4">What We Will Not Do</p>
          <DontList items={[
            "We do not trim cards.",
            "We do not recolor cards.",
            "We do not manufacture corners.",
            "We do not add material to conceal damage.",
            "We do not intentionally misrepresent condition.",
            "We do not promise that cleaning will increase monetary value.",
            "We do not promise that cleaning will increase a grade.",
            "We do not promise that a collectible will be accepted by a grading company.",
            "We do not promise PSA 10s.",
            "We do not promise any specific grade from any grading company.",
          ]} />
        </div>
      </Wrap>

      {/* Fraud / tampering */}
      <Wrap className="mt-16">
        <Callout>
          <p className="eyebrow mb-3 flex items-center gap-2"><ShieldAlert size={13} className="text-[color:var(--magenta)]" /> Fraud, Tampering & Misrepresentation</p>
          <H2>WE PROTECT OUR CLIENTS.<br />WE ALSO PROTECT OUR LAB.</H2>
          <Prose className="mt-6">
            <P>
              Attempts to manipulate CardScience HQ’s submission process, substitute collectibles, falsely
              represent condition, falsely represent package contents, submit fraudulent shipping or insurance
              claims, interfere with documentation, manufacture evidence, misrepresent declared value, threaten
              staff for financial gain or otherwise misuse the CardScience HQ process may result in immediate
              termination of service.
            </P>
            <P>CardScience HQ reserves the right to preserve relevant evidence including:</P>
          </Prose>
          <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-[color:var(--chrome-2)] list-disc pl-5">
            {[
              "Intake photographs", "Intake video", "Processing photographs", "Processing video",
              "Packing video", "Shipping records", "Tracking records", "Weight records",
              "Submission information", "Customer communications", "Payment information where legally permitted",
              "Insurance documentation", "Other relevant business records",
            ].map((t) => <li key={t}>{t}</li>)}
          </ul>
          <P className="mt-6">
            Where CardScience HQ reasonably believes fraud, theft, tampering or another unlawful act may have
            occurred, the company may report the matter to the appropriate carrier, insurer, payment provider,
            grading company, law-enforcement agency or other appropriate authority and may pursue available
            civil remedies.
          </P>
          <p className="mt-4 text-white font-semibold">
            CardScience HQ may refer suspected criminal activity to law enforcement and pursue all remedies
            available under applicable law.
          </p>
        </Callout>
      </Wrap>

      <Wrap className="mt-12 flex flex-wrap gap-3">
        <Link to="/shipping-insurance" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">SHIPPING & INSURANCE <ArrowRight size={16} /></Link>
        <Link to="/submit" className="btn-ghost inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">SUBMIT A COLLECTIBLE</Link>
      </Wrap>
    </div>
  );
}
